import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { ReactNode } from 'react';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Network errors (no status property) - retry up to 3 times
          if (!('status' in error)) {
            return failureCount < 3;
          }

          // Don't retry on 4xx client errors
          if (typeof error.status === 'number') {
            if (error.status >= 400 && error.status < 500) {
              return false;
            }
          }

          // Retry up to 3 times for 5xx server errors and other cases
          return failureCount < 3;
        },
      },
    },
  });
}

const queryClient = createQueryClient();

export function QueryProvider({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export { queryClient };
